import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, RefreshCw, Sparkles, BarChart3 } from "lucide-react";
import toast from "react-hot-toast";
import {
  card,
  microLabel,
  sectionTitle,
  accentBarSection,
  bodySecondary,
  motionStaggerChild,
  emptyState,
  iconSize,
} from "../../../../../lib/design-system";
import { cn } from "../../../../../lib/utils";
import {
  listTargets,
  deleteTarget,
  seedDefaultTargets,
  listPOs,
  listPSOs,
} from "../../../../../services/program.service";
import TargetForm from "../TargetForm";

const TargetsTab = ({ programId }) => {
  const [targets, setTargets] = useState([]);
  const [pos, setPos] = useState([]);
  const [psos, setPsos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [tData, pData, psData] = await Promise.all([
        listTargets(programId),
        listPOs(programId),
        listPSOs(programId),
      ]);
      setTargets(tData.targets || []);
      setPos(pData.pos || []);
      setPsos(psData.psos || []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [programId]);

  const lookupOutcome = (target) => {
    if (target.outcomeType === "PO") {
      return pos.find((p) => p._id === target.outcomeId);
    }
    return psos.find((p) => p._id === target.outcomeId);
  };

  const handleSeed = async () => {
    const eligible = pos.length + psos.length;
    if (eligible === 0) {
      toast.error("Define POs / PSOs first.");
      return;
    }
    if (
      !window.confirm(
        `Seed default attainment targets at level 2.5 for ${eligible} outcomes? Idempotent — skips any that already exist.`
      )
    )
      return;
    setSeeding(true);
    try {
      const result = await seedDefaultTargets(programId, { defaultLevel: 2.5 });
      toast.success(
        `Seeded ${result.inserted}, skipped ${result.skipped} (already existed)`
      );
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  const handleDelete = async (target) => {
    if (!window.confirm("Delete this target?")) return;
    try {
      await deleteTarget(programId, target._id);
      toast.success("Target deleted");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || "Delete failed");
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h2 className={cn(sectionTitle, "text-xl sm:text-2xl")}>
            Attainment Targets
          </h2>
          <div className={cn(accentBarSection, "mt-2")} />
          <p className={cn(bodySecondary, "mt-3 max-w-2xl")}>
            Per-PO / per-PSO target attainment levels (0 – 3 scale). NBA
            Tier-2 typically expects targets between 2.0 and 2.5, ramped up
            year-over-year as evidence of continuous improvement (Criterion 8).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            className="px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className={iconSize.md} />
          </button>
          <button
            onClick={handleSeed}
            disabled={seeding}
            className={cn(
              "inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-accent1 text-accent1 font-semibold transition-colors",
              seeding ? "opacity-60 cursor-not-allowed" : "hover:bg-accent2"
            )}
          >
            <Sparkles className={iconSize.md} />
            {seeding ? "Seeding…" : "Seed defaults"}
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 bg-accent1 text-white font-semibold px-4 py-2 rounded-sm hover:opacity-90 transition-opacity"
          >
            <Plus className={iconSize.md} />
            Custom target
          </button>
        </div>
      </div>

      {loading ? (
        <SkeletonList />
      ) : targets.length === 0 ? (
        <div className={emptyState}>
          <div className="mx-auto h-14 w-14 rounded-sm bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
            <BarChart3 className="h-7 w-7 text-gray-400" />
          </div>
          <div className="font-bold text-gray-700 dark:text-gray-200">
            No attainment targets yet
          </div>
          <div className={cn(bodySecondary, "mt-1")}>
            Click "Seed defaults" to set level 2.5 for every PO and PSO at once.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {targets.map((target, i) => {
            const outcome = lookupOutcome(target);
            return (
              <motion.div
                key={target._id}
                {...motionStaggerChild(i, 0)}
                className={cn(card, "p-5 group")}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-1 w-8 bg-accent1 rounded-sm mb-2" />
                    <div className={microLabel}>{target.outcomeType}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="px-2.5 py-1 rounded-sm bg-accent2 text-accent1 text-xs font-semibold">
                        {outcome?.code || "?"}
                      </span>
                      {target.validFromBatch && (
                        <span className="px-2.5 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium">
                          Batch {target.validFromBatch}
                        </span>
                      )}
                    </div>
                    {outcome && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                        {outcome.statement || outcome.category}
                      </p>
                    )}
                    {target.rationale && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                        {target.rationale}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="text-2xl font-black tracking-tight text-accent1">
                      {Number(target.targetLevel).toFixed(1)}
                    </div>
                    <div className={microLabel}>Target Level</div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button
                      onClick={() => handleDelete(target)}
                      className="p-2 rounded-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600"
                      title="Delete"
                      aria-label="Delete"
                    >
                      <Trash2 className={iconSize.sm} />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {showForm && (
        <TargetForm
          programId={programId}
          pos={pos}
          psos={psos}
          onClose={(reload) => {
            setShowForm(false);
            if (reload) fetchAll();
          }}
        />
      )}
    </div>
  );
};

const SkeletonList = () => (
  <div className="space-y-3">
    {[0, 1, 2].map((i) => (
      <div key={i} className={cn(card, "p-5")}>
        <div className="flex gap-4">
          <div className="h-12 w-12 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
            <div className="h-3 w-2/3 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
          </div>
          <div className="h-8 w-12 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);

export default TargetsTab;
